import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector, shallowEqual } from "react-redux"
import { useNavigate } from "react-router"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { DateTime } from "luxon"

import useT from "utils/hooks/useT"
import { Popup } from "components"
import ESignatureModal from "components/Lims/ESignatureModal"
import StatusBadge from "components/Lims/StatusBadge"
import { displaySampleId } from "utils/scripts/sampleId"
import { canSign, isGmpRelaxed } from "state_management/lims/segregationOfDuties"
import {
  limsResults,
  limsSampleQa,
  limsRelease,
  limsSampleQc,
  limsBatchHold,
  limsOos,
  limsReruns,
  limsProductTestPlan,
  limsWorklistTemplates,
  limsSampleWorklists,
  sampleView,
  analysisDefinitions as analysisDefinitionsActions,
  message,
} from "state_management"
// Reject still records a per-sample QA verdict (STOP) under its signature; release no longer does.
import { QA_DECISION } from "state_management/lims/limsResults"

// QA SampleReview (F6, SPEC-038) — REPLACES QA Analysis Review in the LIMS menu. QA disposition is
// now at sample/lot level: this worklist lists every samplenr/lotnr that is COMPLETE (all required
// analyses across all its analysis runs have cleared review, no open OOS/OOT investigation, all
// reruns finished — limsSampleQa.selectQaSampleWorklist). Per row QA can:
//   - "Open in Sample View" — deep-links to the Sample View filtered to the samplenr/lotnr, the
//     GENGENNEMGANG (review) surface: per sample-analysis "go to analysis" → audit trail (F2) +
//     comments, and the whole sample's history. There is NO sign in the Sample View.
//   - Release — the QA e-signature lands HERE (capability "qa", 4-eyes ≠ enterers + reviewers +
//     supervisors). The lot is released and a certificate is issued; the row leaves this queue and
//     appears in Batch History with its certificate-download link (SPEC-064).
//   - Reject — places a signed batch hold on the lot (the whole batch is blocked from sale).
//
// SPEC-064 (#4157) made this the ONE actionable QA surface. The ACTIVE batch holds and their signed
// "Lift hold" moved in from the QA Batch Holds screen, which is gone; the "Released" list moved OUT
// to the new read-only Batch History menu, together with the certificate download and the date
// filter (#4139). The customer's cut runs between what QA can DO and what merely happened, so this
// screen is now exactly the first half: a release queue, and the holds that block it.
//
// R10/R11's rule still binds: a QA-signed action is offered from ONE screen. Batch History shows the
// same released lots and the same holds — with no button that changes anything.
//
// #4110: Release RELEASES, it does not approve. It used to commit a per-sample QA APPROVE on every
// still-pending result first, so one signature did both jobs — which meant the lot-level button
// silently made the per-analysis QA decisions nobody had made yet. Those belong to the per-analysis
// QA step (ResultEntryPanel/QaDecisionSection), which already commits them under its own signature.
// The worklist below only lists lots whose results are ALREADY QA-approved (#4105), so there is
// nothing left for this signature to approve.
//
// The SERVER re-evaluates the whole completeness/release gate at signing time (release.py) — this
// screen's worklist + four-eyes are the fail-fast UX on top of that authoritative gate.
export default function QaSampleReview() {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const sampleGroupsRaw = useSelector((s) => s.sampleView.sampleGroups, shallowEqual)
  const sampleGroups = useMemo(() => sampleGroupsRaw || [], [sampleGroupsRaw])
  const resItems = useSelector((s) => s.limsResults.items, shallowEqual)
  const relItems = useSelector((s) => s.limsRelease.items, shallowEqual)
  const sampleQc = useSelector((s) => s.limsSampleQc, shallowEqual)
  const batchHoldItems = useSelector((s) => s.limsBatchHold.items, shallowEqual)
  const oosItems = useSelector((s) => s.limsOos.items, shallowEqual)
  const rerunItems = useSelector((s) => s.limsReruns.items, shallowEqual)
  const productTestPlan = useSelector((s) => s.limsProductTestPlan, shallowEqual)
  // F9 (SPEC-041): the sample-prep worklist slices the completeness check reads. Absent from this
  // screen's state slice they defaulted to empty, which made "is a required prep worklist signed?"
  // silently answer yes and listed lots the server gate would refuse.
  const worklistTemplateItems = useSelector((s) => s.limsWorklistTemplates.items, shallowEqual)
  const sampleWorklistItems = useSelector((s) => s.limsSampleWorklists.items, shallowEqual)
  // Required-analysis resolution (Product Test Plan → definition names) reads the definitions map.
  const analysisDefs = useSelector((s) => s.analysisDefinitions, shallowEqual)
  const members = useSelector((s) => s.members)

  const [releasing, setReleasing] = useState(null) // the worklist row pending an Approve & release sig
  const [rejecting, setRejecting] = useState(null) // the worklist row pending a Reject (batch hold) sig
  const [lifting, setLifting] = useState(null) // the batch-hold record pending a QA-signed lift, or null
  const [saving, setSaving] = useState(false)

  // SPEC-064: the ACTIVE holds only — a lifted hold has stopped blocking anything, so it is history
  // and belongs to Batch History, not to a work queue. (The release gate, selectIsBatchRejected,
  // already skips lifted holds.)
  const activeHolds = useSelector(limsBatchHold.selectActiveHolds, shallowEqual)
  // The current member's QA gate (capability only — the per-hold four-eyes != rejectedBy is folded in
  // per row below). Drives the ghosted "Lift hold" affordance (GMP: everyone SEES it, only QA acts).
  const qaCapabilityGate = useSelector((s) => canSign(s, { capability: "qa" }), shallowEqual)
  const currentMemberId = useSelector((s) => s.auth.memberId)
  const gmpRelaxed = useSelector(isGmpRelaxed) // SPEC-025: dev-only relaxation of the four-eyes guard

  useEffect(() => {
    // SPEC-021 (B3): QC capabilities ride on the member dump (loaded app-wide) — no fetch here.
    dispatch(limsResults.fetch())
    dispatch(sampleView.fetch())
    dispatch(limsRelease.fetch())
    dispatch(limsSampleQc.fetch())
    dispatch(limsBatchHold.fetch())
    dispatch(limsOos.fetch())
    dispatch(limsReruns.fetch())
    dispatch(limsProductTestPlan.fetch())
    // F9 (SPEC-041): the prep-worklist completeness gate reads both slices.
    dispatch(limsWorklistTemplates.fetch())
    dispatch(limsSampleWorklists.fetch())
    // Required-analysis resolution (Product Test Plan) needs the analysis definitions.
    dispatch(analysisDefinitionsActions.fetch())
  }, [dispatch])

  // A minimal state slice the completeness/worklist + release selectors read (so the memoized
  // selectors recompute only when these collections change).
  const miniState = useMemo(
    () => ({
      sampleView: { sampleGroups },
      limsResults: { items: resItems },
      limsRelease: { items: relItems },
      limsSampleQc: sampleQc,
      limsBatchHold: { items: batchHoldItems },
      limsOos: { items: oosItems },
      limsReruns: { items: rerunItems },
      limsProductTestPlan: productTestPlan,
      limsWorklistTemplates: { items: worklistTemplateItems },
      limsSampleWorklists: { items: sampleWorklistItems },
      analysisDefinitions: analysisDefs,
    }),
    [
      sampleGroups,
      resItems,
      relItems,
      sampleQc,
      batchHoldItems,
      oosItems,
      rerunItems,
      productTestPlan,
      worklistTemplateItems,
      sampleWorklistItems,
      analysisDefs,
    ]
  )

  // The worklist of COMPLETE samplenr/lotnr awaiting a QA decision.
  const worklist = useMemo(() => limsSampleQa.selectQaSampleWorklist(miniState), [miniState])

  const initials = (id) => (id && members[id] && members[id].initials) || "—"
  const fmtDate = (iso) => (iso ? DateTime.fromISO(iso).toFormat("dd/MM/yyyy HH:mm") : "—")

  // K13 (b): may the current user lift THIS hold? Needs the QA capability AND must not be the
  // person who placed the rejection (four-eyes != rejectedBy). Returns the ghost reason key, or null.
  const liftGhostKey = (hold) => {
    if (!qaCapabilityGate.allowed) return "limsLiftGhostCapability"
    // SPEC-025: skip the four-eyes (!= rejectedBy) guard when controls are relaxed (dev only).
    if (!gmpRelaxed && hold.rejectedBy && hold.rejectedBy === currentMemberId) return "limsLiftGhostFourEyes"
    return null
  }

  // K13 (b): lift the active hold under a QA signature with a MANDATORY reason. The original hold
  // record is preserved (the server stamps liftedBy/liftedAt/liftReason); a lifted hold stops
  // blocking release (the gate re-evaluates) and moves to Batch History. Returns the promise so the
  // dialog shows 403/404/409 inline.
  const handleLift = (signature) => {
    const hold = lifting
    setSaving(true)
    return dispatch(limsBatchHold.lift(hold.batchLot, signature))
      .then(() => {
        setLifting(null)
        dispatch(message.success(t("limsLiftDone", { lot: hold.batchLot })))
      })
      .finally(() => setSaving(false))
  }

  // Four-eyes on the release signature: everyone who entered / reviewed / supervised / override-signed
  // one of the group's QC results may not also sign the release (release.py enforces this too).
  const releaseExclusions = useMemo(
    () => (releasing ? limsRelease.selectReleaseExclusions(miniState, releasing.group) : []),
    [releasing, miniState]
  )

  // The lot(s) registered on the rejecting group's sample-analyses — a Reject places a hold on them.
  const rejectLots = useMemo(
    () => (rejecting ? limsRelease.selectGroupBatchLots(miniState, rejecting.group) : []),
    [rejecting, miniState]
  )

  // Release the lot under the QA signature — and ONLY release it (#4110). Returns the promise so
  // the dialog shows a backend 403 (password / capability / four-eyes) or 422 (server release gate)
  // inline; a lot that stopped being releasable between listing and signing surfaces the SPECIFIC
  // reason (#4105) — which analyses are still outstanding, or the hold that blocked — instead of
  // the bare "not READY" that left QA to work it out from the Sample View.
  const handleRelease = (signature) => {
    const row = releasing
    setSaving(true)
    return dispatch(limsRelease.release(row.group, signature))
      .then(() => {
        setReleasing(null)
        dispatch(message.success(t("limsReleaseDone")))
      })
      .catch((err) => {
        if (limsRelease.isReleaseNotReadyError(err)) {
          const { key, params } = limsRelease.releaseNotReadyMessage(err)
          err.message = t(key, params)
        }
        throw err
      })
      .finally(() => setSaving(false))
  }

  // The rejecting group's results still awaiting a QA decision — a lot rejection records a per-sample
  // STOP on each (the QA verdict on the work), then holds the lot.
  const pendingForRejecting = useMemo(() => {
    if (!rejecting) return []
    const sampleIds = new Set((rejecting.group.analyses || []).map((a) => a.sampleId).filter(Boolean))
    return resItems.filter((r) => sampleIds.has(r.sampleId) && r.state === "sent-to-qa")
  }, [rejecting, resItems])

  // Reject the lot under ONE QA signature: commit a per-sample STOP on every still-pending result
  // (the auditable QA verdict on the work), then place a signed batch hold on each registered lot
  // (the BATCH_REJECTED block on the whole lot). Blocked before opening the dialog when no lot is
  // registered — without one there is nothing to hold (mirrors the per-result STOP guard, SPEC-020
  // C3). Returns the promise so the dialog shows 403 inline.
  const handleReject = (signature) => {
    setSaving(true)
    const stopAll = pendingForRejecting.reduce(
      (chain, r) => chain.then(() => dispatch(limsResults.setQaDecision(r.id, QA_DECISION.STOP, signature))),
      Promise.resolve()
    )
    const commit = rejectLots.reduce(
      (chain, lot) => chain.then(() => dispatch(limsBatchHold.hold(lot, { signature }))),
      stopAll
    )
    return commit
      .then(() => {
        setRejecting(null)
        dispatch(message.success(t("limsSampleQaRejectedDone", { sample: rejecting.sampleName || "—" })))
      })
      .finally(() => setSaving(false))
  }

  // Deep-link to the Sample View, filtered to this samplenr/lotnr — the review surface (no sign).
  const openInSampleView = (row) => {
    navigate(`/sample-view?sample=${encodeURIComponent(row.sampleName || "")}`)
  }

  return (
    <div className="LimsQueue">
      <h1 className="LimsQueue-title">{t("limsQaSampleReview")}</h1>

      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsSampleQaPendingHeading")}</h2>
        <p className="LimsQueue-intro">{t("limsSampleQaIntro")}</p>
        {worklist.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsSampleQaEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsCoaProduct")}</th>
                <th>{t("limsCoaSample")}</th>
                <th>{t("limsCoaSampleId")}</th>
                <th>{t("limsSampleQaSignedAnalyses")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {worklist.map((row) => (
                <tr key={row.key}>
                  <td>{row.productName || t("none")}</td>
                  <td>{row.sampleName || "—"}</td>
                  {/* #4145 — DISPLAY. The row's `key` and the release key are built from the RAW
                      value in `buildGroupByObservation` / `limsSampleQa`; only this cell is cut. */}
                  <td>{row.observationId != null && row.observationId !== "" ? displaySampleId(row.observationId) : "—"}</td>
                  <td>{row.signedCount}</td>
                  <td className="LimsQueue-actions">
                    <div className="LimsQueue-actions-inner">
                      <Button variant="outline-secondary" size="sm" onClick={() => openInSampleView(row)}>
                        {t("limsSampleQaOpenInSampleView")}
                      </Button>
                      <Button variant="success" size="sm" disabled={saving} onClick={() => setReleasing(row)}>
                        {t("limsSampleQaRelease")}
                      </Button>
                      <Button variant="outline-danger" size="sm" disabled={saving} onClick={() => setRejecting(row)}>
                        {t("limsSampleQaReject")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      {/* SPEC-064 (#4157): the ACTIVE batch holds, moved here from the retired QA Batch Holds screen.
          They belong beside the release queue because they are the other half of the same job: a held
          lot is the reason a release cannot proceed, and lifting it is QA's action. The lifted ones
          are NOT here — a lifted hold blocks nothing, so it is history (Batch History). */}
      <section className="LimsQueue-section">
        <h2 className="LimsQueue-subtitle">{t("limsBatchHoldsActiveHeading")}</h2>
        {activeHolds.length === 0 ? (
          <p className="LimsQueue-empty">{t("limsBatchHoldsActiveEmpty")}</p>
        ) : (
          <Table striped hover responsive className="LimsQueue-table">
            <thead>
              <tr>
                <th>{t("limsBatchHoldLot")}</th>
                <th>{t("limsBatchHoldRejectedBy")}</th>
                <th>{t("limsBatchHoldRejectedAt")}</th>
                <th>{t("limsBatchHoldReason")}</th>
                <th>{t("limsBatchHoldStatus")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {activeHolds.map((hold) => {
                const ghostKey = liftGhostKey(hold)
                return (
                  <tr key={hold.id}>
                    <td>{hold.batchLot || "—"}</td>
                    <td>{initials(hold.rejectedBy)}</td>
                    <td>{fmtDate(hold.rejectedAt)}</td>
                    <td>{hold.reason || "—"}</td>
                    <td>
                      <StatusBadge variant="fail">{t("limsBatchHoldStatusActive")}</StatusBadge>
                    </td>
                    <td className="LimsQueue-actions">
                      <div className="LimsQueue-actions-inner">
                        <Button
                          variant="outline-warning"
                          size="sm"
                          disabled={saving || !!ghostKey}
                          title={ghostKey ? t(ghostKey) : undefined}
                          onClick={() => setLifting(hold)}
                        >
                          {t("limsLiftAction")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </section>

      {releasing && (
        <ESignatureModal
          title={t("limsSampleQaRelease")}
          requiredCapability="qa"
          excludedMemberIds={releaseExclusions}
          commentContext="qa-release"
          onSign={handleRelease}
          onCancel={() => setReleasing(null)}
        />
      )}

      {rejecting && rejectLots.length === 0 && (
        <Popup nonScrollable title={t("limsSampleQaReject")} onCancel={() => setRejecting(null)}>
          <p className="LimsQueue-intro">{t("limsSampleQaRejectNoLot")}</p>
          <div className="ResultEntryPanel-qa-actions">
            <Button variant="light" onClick={() => setRejecting(null)}>
              {t("close")}
            </Button>
          </div>
        </Popup>
      )}

      {rejecting && rejectLots.length > 0 && (
        <ESignatureModal
          title={t("limsSampleQaReject")}
          requiredCapability="qa"
          requireReason
          excludedMemberIds={releaseExclusions}
          commentContext="qa-release"
          onSign={handleReject}
          onCancel={() => setRejecting(null)}
        />
      )}

      {/* K13 (b): the QA lift signature — mandatory reason, four-eyes excludes the rejector. */}
      {lifting && (
        <ESignatureModal
          title={t("limsLiftAction")}
          requiredCapability="qa"
          requireReason
          excludedMemberIds={lifting.rejectedBy ? [lifting.rejectedBy] : []}
          onSign={handleLift}
          onCancel={() => setLifting(null)}
        />
      )}
    </div>
  )
}
